/* eslint-disable array-callback-return */
import { call, put, select } from 'redux-saga/effects';

import * as types from './user.types';
import * as actions from './user.actions';
import { apiRequest } from '../../api/api.saga-services';

import * as api from '../../api/request-config/user.api';
import * as apiSetting from '../../api/request-config/user-settings.api';

import * as taskService from '../task/task.saga-service';
import * as sessionService from '../session/session.saga-service';

import { selectWithParams } from '../../../utils/selector.utils';
import * as userGroupTypes from '../user-group/user-group.types';
import { userSelectors } from './user.selectors';
import { ITask } from '../task/task.types';
import { ISession } from '../session/session.types';
import { addFlashMessage } from '../../../utils/flashMessages.utils';
import { authSelectors } from '../../auth/auth.selectors';

export function* storeUser(user: types.IUser | types.IUser[]) {
  yield put(actions.storeUser(user));
  return user;
}

export function* populateUsers(user: types.IUser) {
  const userExists = (yield selectWithParams(userSelectors.selectResourceById, user.id)) as types.IUser | null;

  const users = (yield call(apiRequest, api.searchUserApi())) as types.IUser[];

  const usersToStore = users.map((u) => {
    if (u.id === userExists?.id) {
      return {
        ...u,
        taskIds: userExists?.taskIds,
        sessionIds: userExists?.sessionIds,
      };
    } else {
      return { ...u, taskIds: null, sessionIds: null };
    }
  });

  yield call(storeUser, usersToStore);
  yield call(storeUser, { ...user });
}

export function* clearUser(userId: types.IUser['id']) {
  yield put(actions.clearUser(userId));
}
export function* clearAllUsers() {
  const userIds: types.IUser['id'][] = yield select(userSelectors.selectResourceIds);
  yield put(actions.clearUser(userIds));
}
/* This method sends api request and handle response */
export function* searchUsers(searchParams?: types.IUserSearchParams) {
  const users = (yield call(apiRequest, api.searchUserApi({ includeAll: true, ...searchParams }))) as types.IUser[];
  users.sort((a, b) => (a.firstName < b.firstName ? -1 : 1));

  if (!users) {
    return;
  }

  const usersToStore = users.map((user) => ({ ...user, taskIds: null, sessionIds: null }));
  yield call(storeUser, usersToStore);
  return users;
}

export function* populateTasks(userId: types.IUser['id']) {
  let user = (yield selectWithParams(userSelectors.selectResourceById, userId)) as types.IUser;

  const taskIds = (yield call(taskService.searchTasks, { userId })) as ITask['id'][];

  user = yield call(storeUser, { ...user, taskIds });

  return user;
}

export function* setTasks(userId: types.IUser['id'], taskIds: ITask['id'][]) {
  const user = (yield selectWithParams(userSelectors.selectResourceById, userId)) as types.IUser;

  if (!user) {
    return;
  }
  yield call(storeUser, { ...user, taskIds });

  return user;
}

export function* populateSessions(userId: types.IUser['id']) {
  let user = (yield select(userSelectors.selectResourceById, userId)) as types.IUser;
  if (!user) {
    return;
  }
  if (!user.sessionIds) {
    const sessionIds = (yield call(sessionService.getSessions, { userId })) as ISession['id'][];
    user = yield call(storeUser, { ...user, sessionIds });
  }
  return user;
}

export function* setSessions(userId: types.IUser['id'], sessionIds: ISession['id'][]) {
  const user = (yield select(userSelectors.selectResourceById, userId)) as types.IUser;
  if (!user) {
    return;
  }
  yield call(storeUser, { ...user, sessionIds });
}

export function* createUser(data: types.IUserCreate) {
  const user = (yield call(apiRequest, api.createUserApi(data))) as types.IUser;
  if (!user) {
    return;
  }
  yield call(storeUser, user);
  return user.id;
}

export function* updateUser(data: types.IUserUpdate) {
  const user = (yield call(apiRequest, api.updateUserApi(data))) as types.IUser;
  if (!user) {
    return;
  }
  yield call(storeUser, user);
  return user.id;
}

export function* changePasswordUserProfile(password: types.IChangePasswordUserData['password']) {
  yield call(apiRequest, apiSetting.changePasswordUserSettingsApi(password));
}

export function* updateUserProfile(data: types.IUpdateUserProfileData) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as types.IUser['id'];
  const oldUserInfo = (yield select(userSelectors.selectResourceById, loggedUserId)) as types.IUser;
  const user = (yield call(apiRequest, api.updateUserProfileApi(data))) as types.IUser;
  if (user) {
    // Update the user in the store
    yield call(storeUser, { ...oldUserInfo, ...user });
    // notify in the UI
    yield call(addFlashMessage, { type: 'success', message: 'Your profile has been updated.' });
  }
}

export function* deleteUser(userId: types.IUser['id']) {
  return (yield call(apiRequest, api.deleteUserApi(userId))) as types.IUser | null;
}

export function* removeGroupFromUsers(removedGroupId: userGroupTypes.IUserGroup['id']) {
  const users = (yield select(userSelectors.selectResourceList)) as types.IUser[];

  for (let u = 0; u < users.length; u++) {
    const user = users[u];
    const updatedGroupIds = user.groupIds.filter((groupId) => groupId !== removedGroupId);
    // Update the user in the store
    yield call(storeUser, { ...user, groupIds: updatedGroupIds });
  }
}

// export function* deleteAccount(clientId: IClient['id'], accountId: types.IAccount['id']) {
//   const account = (yield call(apiRequest, api.deleteAccountApi(clientId, accountId))) as types.IAccount;

//   if (!account) {
//     return;
//   }

//   yield call(clearAccount, accountId);
//   return accountId;
// }
